import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Form, notification } from "antd";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { ADMIN_ROUTE, ASSESSMENT_TEMPLATES_ROUTE, NEW_ROUTE } from "@/routing/AppRouter/routes.constants";
import { AssessmentTemplateHttpService } from "@/modules/assessments/services/assessment-template-http.service";

import { NewTemplateMetadataSection } from "./NewTemplateMetadataSection";
import { TemplateForm, SubmitOutcome } from "./TemplateForm";
import { TemplateFormIntro } from "./TemplateFormIntro";
import { useTemplateTestDetails } from "./useTemplateTestDetails";
import { useAllClientsHook } from "./hooks/useAllClients.hook";
import { ALL_CLIENTS, TemplateFormData } from "./types";
import { CREATED_URL_PARAM } from "../AssessmentTemplatesList";

const NOTES = [
  "This form should be used only after all test content, instructions, and norms that you want to use have already been created.",
  "Only use this if you want to create a brand new assessment template. If you want to replace an existing template, use Create New Version instead.",
];

export default function CreateTemplate() {
  const navigate = useNavigate();
  const [form] = Form.useForm<TemplateFormData>();
  const testDetails = useTemplateTestDetails(form);
  const { clients, isLoading: isLoadingClients, hasError: hasClientsError } = useAllClientsHook();

  const clientAbility = useContext(ClientAbilityContext);
  const canCreateTemplate = clientAbility.can(Action.Create, Subject.ASSESSMENT_TEMPLATE);
  useRouteAccessController({ slug: NEW_ROUTE, hasPermission: canCreateTemplate });

  const handleSubmit = async (formValues: TemplateFormData): Promise<SubmitOutcome> => {
    const clientId = formValues.clientId === ALL_CLIENTS ? undefined : formValues.clientId;
    const tests = formValues.tests.map((test, index) => {
      if (test.normId == null) {
        // list-level validators should have already blocked submit if any row is
        // missing a norm, but checking to avoid non-null assertion.
        throw new Error("Template submission reached onFinish with a row missing a norm.");
      }
      return { testId: test.testId, normId: test.normId, rank: index + 1 };
    });

    const trimmedLandingText = (formValues.landingText ?? "").trim();
    const result = await AssessmentTemplateHttpService.createNewTemplate({
      templateName: formValues.templateName.trim(),
      clientId,
      landingPageText: trimmedLandingText ? { "en-US": trimmedLandingText } : undefined,
      tests,
      formulas: formValues.formulas ?? "",
      landingPageImage: formValues.landingImageFile?.originFileObj,
      zipFile: formValues.zipFile,
    });

    if (!result.success) {
      return { kind: "validation-failure", errors: result.errors };
    }

    const clientName = clientId ? clients.find((client) => client.clientId === clientId)?.name : null;
    const params = new URLSearchParams({ [CREATED_URL_PARAM.TEMPLATE_NAME]: result.templateName });
    if (clientName) {
      params.set(CREATED_URL_PARAM.CLIENT_NAME, clientName);
    }

    notification.success({ message: `Template "${result.templateName}" created successfully.` });
    navigate(`/${ADMIN_ROUTE}/${ASSESSMENT_TEMPLATES_ROUTE}?${params.toString()}`);
    return { kind: "success" };
  };

  return (
    <TemplateForm
      pageTitle="New Assessment Template"
      submittingTitle="Creating Assessment Template"
      errorModalTitle="Assessment Template Creation Failed"
      form={form}
      testDetails={testDetails}
      initialValues={{ templateName: "", landingText: "", tests: [], formulas: "" }}
      onSubmit={handleSubmit}
    >
      <TemplateFormIntro notes={NOTES} />
      <NewTemplateMetadataSection
        clients={clients}
        isLoadingClients={isLoadingClients}
        hasClientsError={hasClientsError}
      />
    </TemplateForm>
  );
}
